import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { getFileOrUrlPreview } from 'shared/utils/file-handling/file-handling-utils';
import { useThemeContext } from 'shared/contexts/theme-provider';
import type { UseSettingsEditThemeSyncParams } from './use-settings-edit-theme-sync.types';

const useSettingsEditThemeSync = ({
  formMethods,
  originalThemeRef,
}: UseSettingsEditThemeSyncParams): void => {
  const { setTheme } = useThemeContext();
  const colorValues = useWatch({
    control: formMethods.control,
    name: [
      'colors.primaryColor',
      'colors.secondaryColor',
      'colors.primaryAccent',
      'colors.secondaryAccent',
    ],
  });

  const logoValues = useWatch({
    control: formMethods.control,
    name: ['logo.portalLogoWide', 'logo.portalLogoSquare'],
  });

  useEffect(() => {
    const [primaryColor, secondaryColor, primaryAccent, secondaryAccent] = colorValues ?? [];
    const [portalLogoWide, portalLogoSquare] = logoValues ?? [null, null];
    const originalLogo = originalThemeRef.current.logo;

    const wide = getFileOrUrlPreview(portalLogoWide, originalLogo?.portalLogoWideUrl);
    const square = getFileOrUrlPreview(portalLogoSquare, originalLogo?.portalLogoSquareUrl);

    const logo =
      wide.url && square.url
        ? { portalLogoWideUrl: wide.url, portalLogoSquareUrl: square.url }
        : undefined;

    setTheme({
      primaryColor: primaryColor ?? '',
      secondaryColor: secondaryColor ?? '',
      primaryAccent: primaryAccent ?? '',
      secondaryAccent: secondaryAccent ?? '',
      logo,
    });

    return () => {
      wide.revoke?.();
      square.revoke?.();
    };
  }, [colorValues, logoValues, setTheme, originalThemeRef]);
};

export { useSettingsEditThemeSync };
